import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Sparkle, ShieldCheck, Waveform, ChartLineUp, Eye, Globe,
  Palette, Sun, Moon, Play, CaretDown, CheckCircle, Upload, MagicWand,
  GitBranch, PenNib, Users, Buildings, Handshake, Star, CalendarBlank,
  PaperPlaneTilt, EnvelopeSimple, Phone, MapPin, Scales, Gauge,
  DeviceMobile, Download, WifiHigh,
  LockKey, Heartbeat, Bank, Factory, BriefcaseMetal, Student, TestTube,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/contexts/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { GlassCard } from "@/components/glass/GlassCard";
import { useTheme, THEMES } from "@/contexts/ThemeContext";
import api from "@/lib/api";
import { toast } from "sonner";
import Brand from "@/components/Brand";

const easeOut = [0.16, 1, 0.3, 1];

const ANIM = {
  fadeUp: {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.7, ease: easeOut },
  },
};

/* ------------- THEME SWITCHER (floating) ------------- */
function ThemeSwitcher() {
  const { theme, setTheme, mode, toggleMode } = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        data-testid="landing-theme-switcher-btn"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="flex items-center gap-2 rounded-full border border-[var(--border-strong)] bg-[var(--surface-strong)] backdrop-blur px-3 h-9 text-xs font-medium hover:bg-[var(--surface-2)] transition"
      >
        <Palette size={14} weight="duotone" />
        <span className="hidden md:inline">Theme</span>
        <CaretDown size={12} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="absolute right-0 top-11 z-30 w-64 rounded-xl border border-[var(--border-strong)] bg-[var(--bg-2)] backdrop-blur-xl shadow-xl p-3"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-[var(--text-3)]">Mode</div>
              <button
                data-testid="landing-theme-mode-toggle"
                onClick={toggleMode}
                className="flex items-center gap-2 text-xs px-2 h-7 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-2)]"
              >
                {mode === "dark" ? <Moon size={12} weight="fill" /> : <Sun size={12} weight="fill" />}
                {mode}
              </button>
            </div>
            <div className="text-xs uppercase tracking-widest text-[var(--text-3)] mb-2">Theme</div>
            <div className="grid grid-cols-2 gap-2">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`landing-theme-${t.id}`}
                  onClick={() => { setTheme(t.id); setOpen(false); }}
                  className={`group flex items-center gap-2 p-2 rounded-lg border transition text-left ${
                    theme === t.id ? "border-[var(--accent)]" : "border-[var(--border)] hover:border-[var(--border-strong)]"
                  }`}
                >
                  <span
                    className="w-5 h-5 rounded-md border border-[var(--border-strong)] shrink-0"
                    style={{ background: `linear-gradient(135deg, ${t.swatch} 50%, ${t.accent} 50%)` }}
                  />
                  <span className="text-[11px] font-medium leading-tight">{t.name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------- NAV ------------- */
function Nav() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'product' | 'solutions' | null
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", h); return () => window.removeEventListener("scroll", h);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!openMenu) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openMenu]);

  const scrollTo = (id) => {
    setOpenMenu(null);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Aggregated nav: 2 dropdowns + 2 direct links — works in every language without crowding.
  const groups = [
    {
      id: "product",
      label: t("landing.nav.product"),
      items: [
        { id: "features",      label: t("landing.nav.features") },
        { id: "how",           label: t("landing.nav.howitworks") },
        { id: "walkthrough",   label: t("landing.walkthrough.eyebrow") },
        { id: "mobile",        label: t("landing.nav.mobile") },
      ],
    },
    {
      id: "solutions",
      label: t("landing.nav.solutions"),
      items: [
        { id: "industries", label: t("landing.nav.industries") },
        { id: "about",      label: t("landing.nav.who") },
      ],
    },
  ];
  const directLinks = [
    { id: "pricing", label: t("landing.nav.pricing") },
    { id: "contact", label: t("landing.nav.contact") },
  ];

  return (
    <nav
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[var(--bg)]/75 border-b border-[var(--border)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
          <Brand size={32} />
          <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Clariva</div>
        </Link>

        <div ref={wrapRef} className="hidden md:flex items-center gap-1">
          {groups.map((g) => (
            <div key={g.id} className="relative">
              <button
                data-testid={`landing-nav-group-${g.id}`}
                onClick={() => setOpenMenu(openMenu === g.id ? null : g.id)}
                onMouseEnter={() => setOpenMenu(g.id)}
                aria-haspopup="menu"
                aria-expanded={openMenu === g.id}
                className="text-sm px-3 h-9 rounded-md text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition inline-flex items-center gap-1"
              >
                {g.label}
                <CaretDown size={11} className={`transition-transform ${openMenu === g.id ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {openMenu === g.id && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.14 }}
                    onMouseLeave={() => setOpenMenu(null)}
                    className="absolute top-10 start-0 w-56 rounded-xl border border-[var(--border-strong)] bg-[var(--bg-2)] backdrop-blur-xl shadow-xl py-2 z-30"
                    role="menu"
                    data-testid={`landing-nav-menu-${g.id}`}
                  >
                    {g.items.map((it) => (
                      <button
                        key={it.id}
                        role="menuitem"
                        data-testid={`landing-nav-${it.id}`}
                        onClick={() => scrollTo(it.id)}
                        className="w-full text-start px-3 py-2 text-sm text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition"
                      >
                        {it.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          {directLinks.map((it) => (
            <button
              key={it.id}
              data-testid={`landing-nav-${it.id}`}
              onClick={() => scrollTo(it.id)}
              className="text-sm px-3 h-9 rounded-md text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition"
            >
              {it.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/login"
            data-testid="nav-test-demo-btn"
            className="hidden lg:inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md border border-[var(--border-strong)] text-[var(--text)] hover:bg-[var(--surface)] transition"
          >
            <Play size={12} weight="fill" />
            <span className="whitespace-nowrap">{t("landing.nav.testdemo")}</span>
          </Link>
          <LanguageSwitcher compact />
          <ThemeSwitcher />
          <Link to="/login" data-testid="nav-login-btn" className="hidden sm:block">
            <Button variant="ghost" size="sm">{t("common.signin")}</Button>
          </Link>
          <Link to="/register" data-testid="nav-register-btn">
            <Button size="sm" className="gap-2">{t("common.getstarted")} <ArrowRight size={14} /></Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

/* ------------- HERO ------------- */
function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  return (
    <section ref={ref} className="relative pt-28 md:pt-36 pb-16 md:pb-28 overflow-hidden">
      {/* ambient gradient blobs */}
      <motion.div style={{ y }} className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-20 left-[5%] w-[480px] h-[480px] rounded-full blur-3xl opacity-25" style={{ background: "var(--accent)" }} />
        <div className="absolute top-40 right-[8%] w-[380px] h-[380px] rounded-full blur-3xl opacity-20" style={{ background: "#A78BFA" }} />
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div {...ANIM.fadeUp} className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--border-strong)] bg-[var(--surface)] px-3 h-7 text-[11px] uppercase tracking-widest text-[var(--text-2)] mb-6">
            <Sparkle size={11} weight="fill" className="text-[var(--accent)]" />
            {t("landing.hero.eyebrow")}
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-semibold tracking-tighter leading-[0.95] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            {t("landing.hero.title")}
          </h1>
          <p className="text-base md:text-lg text-[var(--text-2)] max-w-2xl leading-relaxed mb-8">
            {t("landing.hero.subtitle")}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register">
              <Button data-testid="hero-cta-start" size="lg" className="gap-2 px-6 h-12">
                {t("common.startfree")} <ArrowRight size={16} />
              </Button>
            </Link>
            <button
              data-testid="hero-cta-walkthrough"
              onClick={() => document.getElementById("walkthrough")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-[var(--border-strong)] bg-[var(--surface)] hover:bg-[var(--surface-2)] transition text-sm font-medium"
            >
              <Play size={14} weight="fill" /> {t("common.watch")}
            </button>
            <button
              data-testid="hero-cta-book-demo"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-[var(--border)] hover:border-[var(--border-strong)] transition text-sm font-medium"
            >
              <CalendarBlank size={14} weight="duotone" /> {t("common.bookdemo")}
            </button>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-[11px] uppercase tracking-widest text-[var(--text-3)]">
            <span className="flex items-center gap-1.5"><ShieldCheck size={13} weight="duotone" className="text-[var(--accent)]" /> {t("landing.hero.badge.soc")}</span>
            <span className="flex items-center gap-1.5"><Globe size={13} weight="duotone" className="text-[var(--accent)]" /> {t("landing.hero.badge.lang")}</span>
            <span className="flex items-center gap-1.5"><LockKey size={13} weight="duotone" className="text-[var(--accent)]" /> {t("landing.hero.badge.sso")}</span>
          </div>
        </motion.div>

        {/* Product mock card */}
        <motion.div
          initial={{ opacity: 0, y: 40, rotate: -2 }}
          whileInView={{ opacity: 1, y: 0, rotate: -1.5 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: easeOut, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <GlassCard strong className="p-5 relative overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Live clause analysis</div>
                <div className="text-sm font-semibold mt-0.5">Master Service Agreement · v3.2</div>
              </div>
              <span className="chip chip-warning text-[10px]">Medium risk</span>
            </div>
            {[
              { label: "Limitation of liability", risk: "high", score: 82, color: "#EF4444" },
              { label: "Payment terms Net-30", risk: "low", score: 18, color: "#10B981" },
              { label: "Auto-renewal (silent)", risk: "medium", score: 54, color: "#F59E0B" },
              { label: "Termination for convenience", risk: "low", score: 24, color: "#10B981" },
            ].map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="flex items-center gap-3 py-2"
              >
                <div className="w-1.5 h-6 rounded-full" style={{ background: c.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{c.label}</div>
                  <div className="h-1 rounded-full mt-1 bg-[var(--surface-2)] overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${c.score}%` }}
                      transition={{ delay: 0.8 + i * 0.1, duration: 0.9, ease: easeOut }}
                      style={{ background: c.color }}
                      className="h-full"
                    />
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">{c.risk}</span>
              </motion.div>
            ))}
            <div className="mt-4 pt-4 border-t border-[var(--border)] flex items-center gap-2">
              <Waveform size={14} weight="duotone" className="text-[var(--accent)]" />
              <span className="text-[11px] text-[var(--text-2)]">60-sec audio briefing ready</span>
              <span className="ml-auto text-[11px] font-mono text-[var(--text-3)]">0:58</span>
            </div>
          </GlassCard>
          {/* float small card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="absolute -bottom-6 -left-6 hidden md:block"
          >
            <GlassCard strong className="p-3 w-52">
              <div className="flex items-center gap-2 mb-1.5">
                <ChartLineUp size={14} weight="duotone" className="text-emerald-500" />
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Impact score</div>
              </div>
              <div className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>+$284k</div>
              <div className="text-[10px] text-[var(--text-2)]">expected uplift, 18mo</div>
            </GlassCard>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------- TRUSTED BY ------------- */
function Marquee() {
  const brands = ["Harmonic Wave", "CloudMosaic", "NordicLedger", "Arcadia Health", "StellarStake", "Vertex Counsel"];
  return (
    <section className="py-10 border-y border-[var(--border)] bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)] mb-5 text-center">Trusted by fast-moving legal & ops teams</div>
        <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4 opacity-70">
          {brands.map((b) => (
            <div key={b} className="text-sm font-medium tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{b}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------- FEATURES ------------- */
function Features() {
  const { t } = useI18n();
  const features = [
    { icon: Eye,         titleKey: "landing.feat.signatory.title",    descKey: "landing.feat.signatory.desc",    color: "text-sky-500",     key: "signatory" },
    { icon: Waveform,    titleKey: "landing.feat.audio.title",        descKey: "landing.feat.audio.desc",        color: "text-emerald-500", key: "audio" },
    { icon: ChartLineUp, titleKey: "landing.feat.impact.title",       descKey: "landing.feat.impact.desc",       color: "text-amber-500",   key: "impact" },
    { icon: GitBranch,   titleKey: "landing.feat.workflows.title",    descKey: "landing.feat.workflows.desc",    color: "text-fuchsia-500", key: "workflows" },
    { icon: ShieldCheck, titleKey: "landing.feat.obligations.title",  descKey: "landing.feat.obligations.desc",  color: "text-rose-500",    key: "obligations" },
    { icon: Scales,      titleKey: "landing.feat.compliance.title",   descKey: "landing.feat.compliance.desc",   color: "text-teal-500",    key: "compliance" },
    { icon: Sparkle,     titleKey: "landing.feat.ai.title",           descKey: "landing.feat.ai.desc",           color: "text-violet-500",  key: "ai" },
    { icon: Globe,       titleKey: "landing.feat.global.title",       descKey: "landing.feat.global.desc",       color: "text-cyan-500",    key: "global" },
    { icon: Users,       titleKey: "landing.feat.counterparty.title", descKey: "landing.feat.counterparty.desc", color: "text-blue-500",    key: "counterparty" },
  ];
  return (
    <section id="features" className="max-w-7xl mx-auto px-6 py-24">
      <motion.div {...ANIM.fadeUp} className="max-w-2xl mb-14">
        <div className="overline mb-3">{t("landing.features.eyebrow")}</div>
        <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          {t("landing.features.title")}
        </h2>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.map((f, i) => (
          <motion.div
            key={f.key}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
            data-testid={`landing-feature-${f.key}`}
          >
            <GlassCard hover className="p-6 h-full">
              <f.icon size={26} weight="duotone" className={f.color} />
              <div className="mt-4 text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{t(f.titleKey)}</div>
              <div className="mt-2 text-sm text-[var(--text-2)] leading-relaxed">{t(f.descKey)}</div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ------------- HOW IT WORKS (PROCESS FLOW) ------------- */
function HowItWorks() {
  const { t } = useI18n();
  const steps = [
    { icon: Upload, key: "s1" },
    { icon: MagicWand, key: "s2" },
    { icon: ChartLineUp, key: "s3" },
    { icon: Waveform, key: "s4" },
    { icon: GitBranch, key: "s5" },
    { icon: PenNib, key: "s6" },
  ];
  return (
    <section id="how" className="relative py-24 border-y border-[var(--border)] bg-[var(--bg-2)]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...ANIM.fadeUp} className="max-w-2xl mb-14">
          <div className="overline mb-3">{t("landing.process.eyebrow")}</div>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            {t("landing.process.title.l1")}<br />{t("landing.process.title.l2")}
          </h2>
          <p className="mt-4 text-base text-[var(--text-2)]">{t("landing.process.subtitle")}</p>
        </motion.div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-8 left-8 right-8 h-px" style={{ background: "linear-gradient(90deg, transparent, var(--border-strong), transparent)" }} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {steps.map((s, i) => (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6, ease: easeOut }}
                data-testid={`landing-howitworks-step-${i}`}
                className="relative"
              >
                <GlassCard className="p-5 h-full">
                  <div className="relative w-10 h-10 rounded-full border border-[var(--border-strong)] bg-[var(--surface-strong)] flex items-center justify-center mb-4">
                    <s.icon size={18} weight="duotone" className="text-[var(--accent)]" />
                    <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[var(--accent)] text-[var(--primary-fg)] text-[10px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <div className="text-sm font-semibold tracking-tight mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>{t(`landing.process.${s.key}.title`)}</div>
                  <div className="text-xs text-[var(--text-2)] leading-relaxed">{t(`landing.process.${s.key}.desc`)}</div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------- WALKTHROUGH VIDEO ------------- */
function Walkthrough() {
  const { t, formatMoney } = useI18n();
  const [playing, setPlaying] = useState(false);
  return (
    <section id="walkthrough" className="max-w-7xl mx-auto px-6 py-24">
      <motion.div {...ANIM.fadeUp} className="max-w-2xl mb-10">
        <div className="overline mb-3">{t("landing.walkthrough.eyebrow")}</div>
        <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          {t("landing.walkthrough.title")}
        </h2>
        <p className="mt-4 text-base text-[var(--text-2)]">{t("landing.walkthrough.subtitle")}</p>
      </motion.div>

      <motion.div {...ANIM.fadeUp}>
        <GlassCard strong className="relative aspect-video overflow-hidden group">
          {playing ? (
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/aircAruvnKk?autoplay=1&rel=0"
              title="Clariva walkthrough"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="landing-walkthrough-iframe"
            />
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--accent)]/20 via-transparent to-[#A78BFA]/20" />
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80"
                alt="Clariva dashboard"
                className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-55 transition"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <button
                  onClick={() => setPlaying(true)}
                  data-testid="landing-walkthrough-play-btn"
                  className="w-20 h-20 rounded-full bg-[var(--primary)] text-[var(--primary-fg)] flex items-center justify-center shadow-2xl hover:scale-110 transition"
                >
                  <Play size={32} weight="fill" />
                </button>
                <div className="text-xs uppercase tracking-widest text-[var(--text)] bg-[var(--bg)]/70 backdrop-blur px-3 h-7 rounded-full inline-flex items-center">
                  {t("landing.walkthrough.duration")}
                </div>
              </div>
            </>
          )}
        </GlassCard>
      </motion.div>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        {[
          { n: t("landing.walkthrough.kpi1.n"), l: t("landing.walkthrough.kpi1.l") },
          { n: t("landing.walkthrough.kpi2.n"), l: t("landing.walkthrough.kpi2.l") },
          // KPI 3: localized currency for "$284k avg savings"
          { n: formatMoney(284000, { decimals: 0 }), l: t("landing.walkthrough.kpi3.l") },
        ].map((k, i) => (
          <GlassCard key={i} className="p-5">
            <div className="text-3xl font-semibold tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>{k.n}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-[var(--text-3)]">{k.l}</div>
          </GlassCard>
        ))}
      </div>
    </section>
  );
}

/* ------------- INDUSTRIES ------------- */
function Industries() {
  const { t } = useI18n();
  const inds = [
    { icon: Heartbeat,     k: "healthcare" },
    { icon: TestTube,      k: "clinical" },
    { icon: ShieldCheck,   k: "insurance" },
    { icon: Bank,          k: "banking" },
    { icon: Buildings,     k: "realestate" },
    { icon: Buildings,     k: "leasing" },
    { icon: Buildings,     k: "property" },
    { icon: Factory,       k: "financial" },
    { icon: Buildings,     k: "saas" },
    { icon: BriefcaseMetal, k: "legal" },
    { icon: Student,       k: "education" },
  ];
  return (
    <section id="industries" className="max-w-7xl mx-auto px-6 py-24">
      <motion.div {...ANIM.fadeUp} className="max-w-2xl mb-12">
        <div className="overline mb-3">{t("landing.industries.eyebrow")}</div>
        <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          {t("landing.industries.title")}
        </h2>
      </motion.div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {inds.map((i, idx) => (
          <motion.div
            key={i.k}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: (idx % 3) * 0.08 }}
            data-testid={`landing-industry-${i.k}`}
          >
            <GlassCard hover className="p-5 h-full flex gap-4">
              <div className="shrink-0 w-11 h-11 rounded-md bg-[var(--surface-strong)] border border-[var(--border-strong)] flex items-center justify-center">
                <i.icon size={20} weight="duotone" className="text-[var(--accent)]" />
              </div>
              <div>
                <div className="text-base font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{t(`landing.ind.${i.k}`)}</div>
                <div className="text-xs text-[var(--text-2)] mt-1 leading-relaxed">{t(`landing.ind.${i.k}.desc`)}</div>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ------------- PRICING ------------- */
// Approximate USD → local-currency rates for marketing display only. These
// don't need to be exact — the section disclosures that billing happens in
// the contracted currency. Rates rounded for clean numbers in each locale.
const FX_VS_USD = {
  USD: 1, EUR: 0.92, GBP: 0.79, AED: 3.67, SAR: 3.75, QAR: 3.64, KWD: 0.31,
  BHD: 0.38, OMR: 0.385, EGP: 49, JOD: 0.71, CHF: 0.88,
  CAD: 1.36, AUD: 1.52, NZD: 1.65, INR: 84, SGD: 1.34,
  MXN: 17, ARS: 870, CLP: 950, COP: 4100, PEN: 3.75,
  JPY: 156, CNY: 7.2, TWD: 32, HKD: 7.8, KRW: 1380,
};

function nicelyRound(amount, currency) {
  // Pick a sensible "marketing" rounding per currency so prices look clean.
  if (amount === 0) return 0;
  if (["JPY", "KRW", "CLP", "COP", "ARS", "VND"].includes(currency)) {
    // Zero-decimal currencies — round to nearest 100 / 1000 depending on size
    const step = amount >= 10000 ? 1000 : 100;
    return Math.round(amount / step) * step;
  }
  if (["INR", "EGP", "MXN"].includes(currency)) {
    return Math.round(amount / 10) * 10;
  }
  if (["BHD", "OMR", "KWD"].includes(currency)) {
    // High-value currencies — decimal places matter
    return Math.round(amount * 10) / 10;
  }
  // Default — round to nearest whole unit
  return Math.round(amount);
}

function Pricing() {
  const { t, currency, formatMoney } = useI18n();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/subscription/plans")
      .then(({ data }) => { if (!cancelled) setPlans(data?.plans || []); })
      .catch(() => { if (!cancelled) setPlans([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Translate an English feature line via key `landing.pricing.feat.<slug>`.
  // Falls back to the original English string when no translation is set —
  // so backend can keep the canonical names without breaking the UI.
  const featLabel = (raw) => {
    const slug = String(raw)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    const key = `landing.pricing.feat.${slug}`;
    const candidate = t(key);
    return candidate === key ? raw : candidate;
  };

  const fxRate = FX_VS_USD[currency] || 1;

  const renderPrice = (plan) => {
    if (plan.id === "enterprise" || plan.price_usd == null) {
      return <div className="text-3xl md:text-4xl font-semibold tracking-tighter" style={{ fontFamily: "var(--font-heading)" }}>{t("landing.pricing.contact")}</div>;
    }
    if (plan.price_usd === 0) {
      return (
        <div>
          <span className="text-5xl font-semibold tracking-tighter" style={{ fontFamily: "var(--font-heading)" }}>{formatMoney(0, { decimals: 0 })}</span>
        </div>
      );
    }
    const local = nicelyRound(plan.price_usd * fxRate, currency);
    return (
      <div className="flex items-baseline gap-1">
        <span className="text-5xl font-semibold tracking-tighter" style={{ fontFamily: "var(--font-heading)" }}>
          {formatMoney(local, { decimals: 0 })}
        </span>
      </div>
    );
  };

  const billingLine = (plan) => {
    if (plan.billing === "forever" || plan.price_usd === 0) return t("landing.pricing.billed.forever");
    if (plan.billing === "contact sales" || plan.price_usd == null) return t("landing.pricing.billed.contact");
    return t("landing.pricing.billed.monthly");
  };

  const ctaLabel = (plan) => {
    if (plan.id === "free") return t("landing.pricing.cta.free");
    if (plan.id === "enterprise") return t("landing.pricing.cta.enterprise");
    return t("landing.pricing.cta.paid");
  };

  const ctaHref = (plan) => (plan.id === "enterprise" ? "#contact" : "/register");
  const planLabel = (plan) => t(`landing.pricing.${plan.id}`) || plan.name;

  if (loading) {
    return (
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-80 rounded-xl border border-[var(--border)] glass-subtle animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-24" data-testid="landing-pricing-section">
      <motion.div {...ANIM.fadeUp} className="max-w-2xl mb-14">
        <div className="overline mb-3">{t("landing.pricing.eyebrow")}</div>
        <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-tight mb-3" style={{ fontFamily: "var(--font-heading)" }}>
          {t("landing.pricing.title")}
        </h2>
        <p className="text-base text-[var(--text-2)] leading-relaxed">{t("landing.pricing.subtitle")}</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans.map((plan, idx) => {
          const popular = plan.popular;
          const seats = plan.limits?.team_seats;
          const contracts = plan.limits?.contracts;
          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.07 }}
              data-testid={`pricing-card-${plan.id}`}
              className="relative"
            >
              {popular && (
                <div className="absolute -top-3 inset-x-0 flex justify-center pointer-events-none z-10">
                  <span className="bg-[var(--accent)] text-[var(--accent-contrast,#fff)] text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full shadow-lg" data-testid={`pricing-popular-${plan.id}`}>
                    {t("landing.pricing.popular")}
                  </span>
                </div>
              )}
              <GlassCard
                strong={popular}
                hover={!popular}
                className={`p-6 h-full flex flex-col ${popular ? "border-[var(--accent)] shadow-[0_0_0_1px_var(--accent)]" : ""}`}
              >
                <div className="mb-5">
                  <div className="text-base font-semibold tracking-tight mb-1" style={{ fontFamily: "var(--font-heading)" }}>
                    {planLabel(plan)}
                  </div>
                  <div className="text-xs text-[var(--text-3)] uppercase tracking-wider min-h-[1.25rem]">{billingLine(plan)}</div>
                </div>

                <div className="mb-6 min-h-[4rem]" data-testid={`pricing-price-${plan.id}`}>
                  {renderPrice(plan)}
                </div>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {(seats != null) && (
                    <li className="flex items-start gap-2 text-xs text-[var(--text-2)]">
                      <Users size={14} className="text-[var(--accent)] shrink-0 mt-0.5" />
                      <span>{seats === -1 ? t("landing.pricing.unlimited") : seats} {t("landing.pricing.seats")}</span>
                    </li>
                  )}
                  {(contracts != null) && (
                    <li className="flex items-start gap-2 text-xs text-[var(--text-2)]">
                      <PenNib size={14} className="text-[var(--accent)] shrink-0 mt-0.5" />
                      <span>{contracts === -1 ? t("landing.pricing.unlimited") : contracts} {t("landing.pricing.contracts")}</span>
                    </li>
                  )}
                  {(plan.features || []).slice(0, 6).map((feat, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-[var(--text-2)]" data-testid={`pricing-feat-${plan.id}-${i}`}>
                      <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
                      <span>{featLabel(feat)}</span>
                    </li>
                  ))}
                </ul>

                <Link to={ctaHref(plan)} data-testid={`pricing-cta-${plan.id}`}>
                  <Button
                    variant={popular ? "default" : "outline"}
                    className="w-full gap-2"
                    size="lg"
                    onClick={(e) => {
                      if (plan.id === "enterprise") {
                        e.preventDefault();
                        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                  >
                    {ctaLabel(plan)} <ArrowRight size={14} />
                  </Button>
                </Link>
              </GlassCard>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-8 text-center text-[11px] text-[var(--text-3)]" data-testid="pricing-fineprint">
        {t("landing.pricing.fineprint", { currency })}
      </div>
    </section>
  );
}

/* ------------- MOBILE APP / QR CODES ------------- */
function MobileApp() {
  const { t } = useI18n();
  // Always point QR codes at the production domain so printed posters, swag and
  // partner decks keep working after preview URLs rotate.
  const origin = "https://clarivaclm.com";
  const appUrl = `${origin}/login?src=qr`;
  const testFlightUrl = `${origin}/login?src=qr-ios`;
  const playUrl = `${origin}/login?src=qr-android`;

  return (
    <section id="mobile" className="relative py-20 md:py-28 border-y border-[var(--border)] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.08] bg-[radial-gradient(at_30%_30%,var(--accent),transparent_50%)]" />
      <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <motion.div {...ANIM.fadeUp} className="lg:col-span-3">
            <div className="overline mb-3 flex items-center gap-2"><DeviceMobile size={14} weight="duotone" className="text-[var(--accent)]" /> {t("landing.mobile.eyebrow")}</div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tighter leading-tight mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
              {t("landing.mobile.title.l1")}<br className="hidden md:block" /> <span className="text-[var(--text-3)]">{t("landing.mobile.title.l2")}</span>
            </h2>
            <p className="text-sm md:text-base text-[var(--text-2)] leading-relaxed max-w-xl mb-6">
              {t("landing.mobile.desc")}
            </p>
            <div className="grid sm:grid-cols-3 gap-3 max-w-lg mb-6">
              <div className="glass-subtle border border-[var(--border)] rounded-md p-3 text-xs flex items-start gap-2"><Download size={14} className="text-[var(--accent)] mt-0.5" /> <div><div className="font-semibold">{t("landing.mobile.feat.install.title")}</div><div className="text-[var(--text-3)] text-[11px] mt-0.5">{t("landing.mobile.feat.install.desc")}</div></div></div>
              <div className="glass-subtle border border-[var(--border)] rounded-md p-3 text-xs flex items-start gap-2"><WifiHigh size={14} className="text-[var(--accent)] mt-0.5" /> <div><div className="font-semibold">{t("landing.mobile.feat.offline.title")}</div><div className="text-[var(--text-3)] text-[11px] mt-0.5">{t("landing.mobile.feat.offline.desc")}</div></div></div>
              <div className="glass-subtle border border-[var(--border)] rounded-md p-3 text-xs flex items-start gap-2"><PaperPlaneTilt size={14} className="text-[var(--accent)] mt-0.5" /> <div><div className="font-semibold">{t("landing.mobile.feat.push.title")}</div><div className="text-[var(--text-3)] text-[11px] mt-0.5">{t("landing.mobile.feat.push.desc")}</div></div></div>
            </div>
          </motion.div>

          <motion.div {...ANIM.fadeUp} className="lg:col-span-2 flex justify-center">
            <GlassCard strong className="p-6 md:p-8 max-w-md w-full" data-testid="landing-mobile-qr-card">
              <div className="text-xs font-semibold text-[var(--accent)] uppercase tracking-wider mb-1 text-center">{t("landing.mobile.qr.title")}</div>
              <div className="text-[11px] text-[var(--text-3)] text-center mb-5">{t("landing.mobile.qr.subtitle")}</div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col items-center">
                  <div className="bg-white p-2.5 rounded-lg shadow-lg" data-testid="landing-qr-ios">
                    <QRCodeSVG value={testFlightUrl} size={132} level="M" includeMargin={false} />
                  </div>
                  <div className="mt-2 text-[11px] font-semibold">{t("landing.mobile.qr.ios")}</div>
                  <div className="text-[10px] text-[var(--text-3)]">{t("landing.mobile.qr.ios.hint")}</div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="bg-white p-2.5 rounded-lg shadow-lg" data-testid="landing-qr-android">
                    <QRCodeSVG value={playUrl} size={132} level="M" includeMargin={false} />
                  </div>
                  <div className="mt-2 text-[11px] font-semibold">{t("landing.mobile.qr.android")}</div>
                  <div className="text-[10px] text-[var(--text-3)]">{t("landing.mobile.qr.android.hint")}</div>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-[var(--border)] text-center">
                <div className="text-[10px] text-[var(--text-3)] uppercase tracking-wider">{t("landing.mobile.qr.fallback")}</div>
                <div className="mt-1 text-xs font-mono text-[var(--accent)] truncate" title={appUrl}>{appUrl}</div>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ------------- TESTIMONIALS ------------- */
function Testimonials() {
  const { t } = useI18n();
  const cards = [
    { id: "t1", rating: 5 },
    { id: "t2", rating: 5 },
    { id: "t3", rating: 5 },
  ];
  return (
    <section className="py-24 border-y border-[var(--border)] bg-[var(--bg-2)]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...ANIM.fadeUp} className="max-w-2xl mb-12">
          <div className="overline mb-3">{t("landing.testimonials.eyebrow")}</div>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            {t("landing.testimonials.title")}
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5">
          {cards.map((q, i) => (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              data-testid={`landing-testimonial-${i}`}
            >
              <GlassCard className="p-6 h-full flex flex-col">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: q.rating }).map((_, s) => (
                    <Star key={s} size={14} weight="fill" className="text-amber-500" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed flex-1">"{t(`landing.testimonials.${q.id}.quote`)}"</p>
                <div className="mt-5 pt-5 border-t border-[var(--border)]">
                  <div className="text-sm font-semibold">{t(`landing.testimonials.${q.id}.name`)}</div>
                  <div className="text-xs text-[var(--text-3)] mt-0.5">{t(`landing.testimonials.${q.id}.role`)}</div>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------- WHO WE ARE ------------- */
function WhoWeAre() {
  const { t } = useI18n();
  const pillars = [
    { icon: Scales, key: "pillar1" },
    { icon: Handshake, key: "pillar2" },
    { icon: Gauge, key: "pillar3" },
  ];
  return (
    <section id="about" className="max-w-7xl mx-auto px-6 py-24">
      <motion.div {...ANIM.fadeUp} className="max-w-3xl mb-14">
        <div className="overline mb-3">{t("landing.who.eyebrow")}</div>
        <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-tight mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
          {t("landing.who.title2")}
        </h2>
        <p className="text-base md:text-lg text-[var(--text-2)] leading-relaxed">
          {t("landing.who.desc2")}
        </p>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-5">
        {pillars.map((p, i) => (
          <motion.div
            key={p.key}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            data-testid={`landing-about-pillar-${i}`}
          >
            <GlassCard hover className="p-6 h-full">
              <p.icon size={28} weight="duotone" className="text-[var(--accent)]" />
              <div className="mt-4 text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{t(`landing.who.${p.key}.title`)}</div>
              <div className="mt-2 text-sm text-[var(--text-2)] leading-relaxed">{t(`landing.who.${p.key}.desc`)}</div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ------------- CONTACT + BOOK eSESSION ------------- */
function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "", email: "", company: "", message: "",
    intent: "demo", preferred_date: "", preferred_time: "10:00",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.name || !form.email) {
      toast.error(t("landing.contact.toast.required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success(form.intent === "esession" ? t("landing.contact.toast.esession") : t("landing.contact.toast.success"));
      setSubmitted(true);
    } catch (e) {
      toast.success(t("landing.contact.toast.success"));
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = submitting
    ? t("landing.contact.submitting")
    : form.intent === "esession" ? t("landing.contact.submit.esession")
    : form.intent === "sales" ? t("landing.contact.submit.sales")
    : t("landing.contact.submit.demo");

  const intents = [
    { id: "demo",     label: t("landing.contact.intent.demo"),     icon: PaperPlaneTilt },
    { id: "esession", label: t("landing.contact.intent.esession"), icon: CalendarBlank  },
    { id: "sales",    label: t("landing.contact.intent.sales"),    icon: Handshake      },
  ];
  const bullets = [
    t("landing.contact.bullet1"), t("landing.contact.bullet2"),
    t("landing.contact.bullet3"), t("landing.contact.bullet4"),
  ];

  return (
    <section id="contact" className="max-w-7xl mx-auto px-6 py-24">
      <div className="grid lg:grid-cols-5 gap-10">
        <motion.div {...ANIM.fadeUp} className="lg:col-span-2">
          <div className="overline mb-3">{t("landing.contact.eyebrow2")}</div>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tighter leading-tight mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
            {t("landing.contact.title2")}
          </h2>
          <p className="text-base text-[var(--text-2)] mb-8 leading-relaxed">
            {t("landing.contact.desc2")}
          </p>
          <ul className="space-y-3 text-sm">
            {bullets.map((x, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle size={16} weight="fill" className="text-[var(--accent)] shrink-0 mt-0.5" />
                <span className="text-[var(--text-2)]">{x}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 space-y-3 text-sm text-[var(--text-2)]">
            <div className="flex items-center gap-3"><EnvelopeSimple size={14} weight="duotone" className="text-[var(--accent)]" /> hello@clariva.ai</div>
            <div className="flex items-center gap-3"><Phone size={14} weight="duotone" className="text-[var(--accent)]" /> +1 (415) 555-0182</div>
            <div className="flex items-center gap-3"><MapPin size={14} weight="duotone" className="text-[var(--accent)]" /> San Francisco · London · Dubai</div>
          </div>
        </motion.div>

        <motion.div {...ANIM.fadeUp} className="lg:col-span-3">
          <GlassCard strong className="p-6 md:p-8">
            {submitted ? (
              <div className="text-center py-10">
                <CheckCircle size={48} weight="duotone" className="mx-auto text-emerald-500 mb-4" />
                <div className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{t("landing.contact.success.title")}</div>
                <p className="text-sm text-[var(--text-2)] max-w-md mx-auto">{t("landing.contact.success.desc")}</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-6 p-1 rounded-lg border border-[var(--border)] bg-[var(--surface)] w-fit">
                  {intents.map((it) => (
                    <button
                      key={it.id}
                      data-testid={`landing-contact-intent-${it.id}`}
                      onClick={() => setForm({ ...form, intent: it.id })}
                      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition ${
                        form.intent === it.id ? "bg-[var(--primary)] text-[var(--primary-fg)]" : "text-[var(--text-2)] hover:text-[var(--text)]"
                      }`}
                    >
                      <it.icon size={12} weight="duotone" /> {it.label}
                    </button>
                  ))}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">{t("landing.contact.form.name")}</Label>
                    <Input data-testid="landing-contact-name" value={form.name} onChange={update("name")} placeholder={t("landing.contact.placeholder.name")} className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-xs">{t("landing.contact.form.email")}</Label>
                    <Input data-testid="landing-contact-email" type="email" value={form.email} onChange={update("email")} placeholder={t("landing.contact.placeholder.email")} className="mt-1.5" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">{t("landing.contact.form.company")}</Label>
                    <Input data-testid="landing-contact-company" value={form.company} onChange={update("company")} placeholder={t("landing.contact.placeholder.company")} className="mt-1.5" />
                  </div>

                  {form.intent === "esession" && (
                    <>
                      <div>
                        <Label className="text-xs">{t("landing.contact.field.preferred_date")}</Label>
                        <Input data-testid="landing-contact-date" type="date" value={form.preferred_date} onChange={update("preferred_date")} className="mt-1.5" />
                      </div>
                      <div>
                        <Label className="text-xs">{t("landing.contact.field.preferred_time")}</Label>
                        <Input data-testid="landing-contact-time" type="time" value={form.preferred_time} onChange={update("preferred_time")} className="mt-1.5" />
                      </div>
                    </>
                  )}

                  <div className="md:col-span-2">
                    <Label className="text-xs">{t("landing.contact.field.message")}</Label>
                    <Textarea data-testid="landing-contact-message" rows={4} value={form.message} onChange={update("message")} placeholder={t("landing.contact.placeholder.message")} className="mt-1.5" />
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                  <div className="text-[11px] text-[var(--text-3)]">{t("landing.contact.privacy")}</div>
                  <Button
                    data-testid="landing-contact-submit"
                    onClick={submit}
                    disabled={submitting}
                    size="lg"
                    className="gap-2 h-11 px-6"
                  >
                    {submitLabel}
                    <ArrowRight size={14} />
                  </Button>
                </div>
              </>
            )}
          </GlassCard>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------- FINAL CTA ------------- */
function FinalCTA() {
  const { t } = useI18n();
  return (
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl opacity-20" style={{ background: "var(--accent)" }} />
      </div>
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.h2
          {...ANIM.fadeUp}
          className="text-4xl md:text-6xl font-semibold tracking-tighter leading-none"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {t("landing.final.title2.l1")}<br /><span className="text-accent-grad">{t("landing.final.title2.l2")}</span>
        </motion.h2>
        <motion.p {...ANIM.fadeUp} className="mt-6 text-base md:text-lg text-[var(--text-2)] max-w-2xl mx-auto">
          {t("landing.final.desc2")}
        </motion.p>
        <motion.div {...ANIM.fadeUp} className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/register">
            <Button data-testid="final-cta-start" size="lg" className="gap-2 px-8 h-14">
              {t("landing.final.cta.start")} <ArrowRight size={18} />
            </Button>
          </Link>
          <Link to="/login">
            <Button
              data-testid="final-cta-demo"
              variant="outline"
              size="lg"
              className="h-14 px-8 border-[var(--border-strong)]"
            >
              {t("landing.final.cta.demo")}
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------- FOOTER ------------- */
function Footer() {
  const { t } = useI18n();
  const cols = [
    { title: t("landing.footer.product"), key: "product" },
    { title: t("landing.footer.company"), key: "company" },
    { title: t("landing.footer.legal"),   key: "legal"   },
  ];
  return (
    <footer className="border-t border-[var(--border)] py-12">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[var(--accent)] to-[var(--primary)] flex items-center justify-center">
              <Sparkle size={16} weight="fill" className="text-[var(--primary-fg)]" />
            </div>
            <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Clariva</div>
          </div>
          <p className="text-xs text-[var(--text-2)] leading-relaxed">{t("landing.footer.tagline2")}</p>
        </div>
        {cols.map((c) => {
          const items = (t(`landing.footer.${c.key}.items`) || "").split(",").filter(Boolean);
          return (
            <div key={c.key}>
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)] mb-4">{c.title}</div>
              <ul className="space-y-2">
                {items.map((it) => (
                  <li key={it}><a className="text-xs text-[var(--text-2)] hover:text-[var(--text)] transition cursor-pointer">{it}</a></li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-10 pt-6 border-t border-[var(--border)] flex justify-between text-[11px] text-[var(--text-3)] flex-wrap gap-3">
        <div>{t("landing.footer.copyright")}</div>
        <div>{t("landing.footer.madein")}</div>
      </div>
    </footer>
  );
}

/* ------------- PAGE ------------- */
export default function Landing() {
  return (
    <div className="min-h-screen overflow-x-hidden relative bg-[var(--bg)] text-[var(--text)]">
      <Nav />
      <Hero />
      <Marquee />
      <Features />
      <HowItWorks />
      <Walkthrough />
      <Industries />
      <Pricing />
      <MobileApp />
      <Testimonials />
      <WhoWeAre />
      <Contact />
      <FinalCTA />
      <Footer />
    </div>
  );
}
